import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Timeline } from 'antd';
import {
    Boxes,
    Package,
    RefreshCw,
    Search,
    X,
    RotateCcw
} from 'lucide-react';
import { setSubLoading } from '../../Redux/Services/AuthSlice';
import { NotificationSound } from '../CommonComponents/CommonNotification';
import { ApiCall, sanitizeInput } from '../CommonComponents/CustomHooks';
import { InputBox } from '../CommonComponents/InputBox';

/* ─────────────────────────────────────────────────────────────
   SUB-COMPONENT: Movement History (Exact AWB Query Style)
───────────────────────────────────────────────────────────── */
export const MovementHistory = ({
    movementDetails = [],
    pickupRequestNo = '',
    loading = false,
    onRefresh,
    onClose
}) => {
    const items = (movementDetails || []).map((row) => {
        const pickupStatus = row?.pickupStatus || row?.PickupStatus || row?.status || row?.Status || '';
        const statusDetails = row?.statusDetails || row?.StatusDetails || '';
        const statusDate = row?.statusDate || row?.StatusDate || '';
        const currentBranch = row?.currentBranch || row?.CurrentBranch || '';
        const statusUpdateBy = row?.statusUpdateBy || row?.StatusUpdateBy || '';

        const dateParts = statusDate.split(' ');
        const dateStr = dateParts[0] || '';
        const timeStr = dateParts.slice(1).join(' ') || '';

        return {
            label: (
                <div className="text-right pr-2">
                    <p className="text-xs font-semibold text-gray-700 m-0 leading-tight">
                        {dateStr}
                    </p>
                    {timeStr && (
                        <p className="text-[11px] text-gray-400 m-0 leading-tight">
                            {timeStr}
                        </p>
                    )}
                </div>
            ),
            children: (
                <div className="pb-1">
                    <p className="text-xs font-bold text-gray-800 m-0 leading-tight">
                        {pickupStatus}
                    </p>
                    {statusDetails && (
                        <p className="text-[11px] text-gray-500 m-0 leading-tight mt-0.5">
                            {statusDetails}
                        </p>
                    )}
                    {(currentBranch || statusUpdateBy) && (
                        <p className="text-[11px] font-medium text-gray-600 m-0 leading-tight mt-0.5">
                            {currentBranch} {statusUpdateBy ? `(${statusUpdateBy})` : ''}
                        </p>
                    )}
                </div>
            )
        };
    });

    return (
        <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-xs">
            {/* ── Red Header Banner (Exact SIPL AWB Style) ── */}
            <div className="flex items-center justify-between px-3 py-1.5 bg-[var(--theme-color,#dc2626)]">
                <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-white tracking-wide">Tracking History</span>
                    {pickupRequestNo && (
                        <span className="text-[10.5px] font-bold px-2 py-0.2 rounded bg-white/20 text-white font-mono">
                            {pickupRequestNo}
                        </span>
                    )}
                </div>

                <div className="flex items-center gap-1.5">
                    {onRefresh && (
                        <button
                            type="button"
                            onClick={onRefresh}
                            disabled={loading}
                            title="Refresh"
                            className="text-white/80 hover:text-white transition-colors cursor-pointer disabled:opacity-50"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                    )}
                    {onClose && (
                        <button
                            type="button"
                            onClick={onClose}
                            title="Close"
                            className="text-white/80 hover:text-white transition-colors cursor-pointer ml-1"
                        >
                            <X className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>
            </div>

            {/* ── Timeline Body ── */}
            <div className="bg-white px-3 pt-3 pb-1 max-h-[250px] overflow-y-auto">
                {loading ? (
                    <div className="py-6 flex flex-col items-center justify-center space-y-2 text-gray-400">
                        <RefreshCw className="w-4 h-4 animate-spin text-[var(--theme-color,#dc2626)]" />
                        <span className="text-[11px] font-medium">Fetching history...</span>
                    </div>
                ) : items.length === 0 ? (
                    <div className="py-6 flex flex-col items-center justify-center space-y-1 text-gray-400">
                        <Package className="w-6 h-6 opacity-40 text-gray-400" />
                        <span className="text-[11px] font-medium">No tracking history found</span>
                    </div>
                ) : (
                    <div className="w-full max-w-xl sm:max-w-2xl ml-8 sm:ml-28 md:ml-48 py-1">
                        <Timeline mode="left" items={items} />
                    </div>
                )}
            </div>
        </div>
    );
};

/* ─────────────────────────────────────────────────────────────
   MAIN COMPONENT: Reusable Pickup Tracking History Card
───────────────────────────────────────────────────────────── */
export const PickupTrackingCard = ({
    pickupRequestNo,
    trackingData,
    loading = false,
    onRefresh,
    onClose
}) => {
    const basic =
        trackingData?.data?.[0]?.basicDetails ||
        trackingData?.data?.[0]?.BasicDetails ||
        trackingData?.basicDetails ||
        trackingData?.BasicDetails ||
        {};

    const movement =
        trackingData?.momentDetails ||
        trackingData?.MomentDetails ||
        [];

    const reqNo = basic?.pickupRequestNo || basic?.PickupRequestNo || pickupRequestNo || '';

    return (
        <MovementHistory
            movementDetails={movement}
            pickupRequestNo={reqNo}
            loading={loading}
            onRefresh={onRefresh}
            onClose={onClose}
        />
    );
};

/* ─────────────────────────────────────────────────────────────
   PAGE COMPONENT: Standalone Pickup Tracking Page
───────────────────────────────────────────────────────────── */
export const PickupTracking = () => {
    const dispatch = useDispatch();
    const inputrefs = useRef({});

    const [pickupRequestNo, setPickupRequestNo] = useState('');
    const [trackingData, setTrackingData] = useState(null);
    const [loading, setLoading] = useState(false);

    const handleSearchTracking = async (requestNoToSearch) => {
        const queryNo = (requestNoToSearch !== undefined ? requestNoToSearch : pickupRequestNo).trim();
        if (!queryNo) {
            await NotificationSound('ERROR', 'Please enter a Pickup Request No.', 'txtPickupRequestNo', inputrefs);
            return;
        }

        try {
            setLoading(true);
            await dispatch(setSubLoading(true));
            const res = await ApiCall('/api/PickupRequest/GetPickupRequestTracking', {
                PickupRequestNo: queryNo
            });

            if (res?.status === 'SUCCESS' || res?.Status === 'SUCCESS') {
                setTrackingData(res);
            } else {
                setTrackingData(null);
                await NotificationSound(res?.status || 'ERROR', res?.message || res?.Message || 'No tracking record found', 'txtPickupRequestNo', inputrefs);
            }
        } catch (error) {
            setTrackingData(null);
            await NotificationSound('ERROR', 'Failed to fetch tracking data', 'txtPickupRequestNo', inputrefs);
        } finally {
            setLoading(false);
            await dispatch(setSubLoading(false));
        }
    };

    const handleReset = () => {
        setPickupRequestNo('');
        setTrackingData(null);
        if (inputrefs.current['txtPickupRequestNo']) {
            inputrefs.current['txtPickupRequestNo']?.focus();
        }
    };

    useEffect(() => {
        document.title = 'Pickup Tracking';
        if (inputrefs.current['txtPickupRequestNo']) {
            inputrefs.current['txtPickupRequestNo']?.focus();
        }
    }, []);

    return (
        <div className="w-full space-y-3">
            {/* Search Card */}
            <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-xs space-y-2.5">
                <div className="flex items-center gap-2 pb-2 border-b border-gray-100">
                    <Boxes className="w-4 h-4 text-[var(--theme-color)]" />
                    <h2 className="text-sm font-bold text-gray-800 tracking-tight">
                        Pickup Request Tracking
                    </h2>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2.5 items-end">
                    <InputBox
                        id="txtPickupRequestNo"
                        label="Pickup Request No"
                        placeholder="e.g. PR1003"
                        required
                        inputrefs={inputrefs}
                        value={pickupRequestNo}
                        onChange={(e) => setPickupRequestNo(sanitizeInput(e.target.value))}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleSearchTracking();
                            }
                        }}
                        divclassname="w-full"
                    />

                    <div className="flex items-center gap-1.5 pt-1">
                        <button
                            type="button"
                            onClick={() => handleSearchTracking()}
                            className="flex items-center gap-1 px-3.5 py-1.5 bg-[var(--theme-color)] text-white rounded-md font-bold shadow-xs uppercase tracking-wider text-[10.5px] hover:opacity-90 transition-all cursor-pointer"
                        >
                            <Search className="w-3 h-3" />
                            <span>TRACK</span>
                        </button>

                        <button
                            type="button"
                            onClick={handleReset}
                            className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md font-bold uppercase tracking-wider text-[10.5px] hover:bg-gray-200 transition-all cursor-pointer"
                        >
                            <RotateCcw className="w-3 h-3" />
                            <span>RESET</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Tracking History Display */}
            {trackingData && (
                <PickupTrackingCard
                    pickupRequestNo={pickupRequestNo}
                    trackingData={trackingData}
                    loading={loading}
                    onRefresh={() => handleSearchTracking()}
                    onClose={() => setTrackingData(null)}
                />
            )}
        </div>
    );
};

export default PickupTracking;
